import React, { useCallback } from "react";

import { useTranslation } from "react-i18next";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";

import { getClientFeatureQuery } from "../../../../config/graphql/query";
import { TOGGLE_CLIENT_FEATURE } from "../../../../config/graphql/mutation";

import { useClientId } from "../hooks/useClient";

import { ToggleFeatureButton } from "../Shelly/ShellyControllers";
import useHasRole from "../../../../lib/hooks/useHasRole";

const DocumentScannerFeature: React.FC = () => {
  const { t } = useTranslation(["common"]);

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);

  const { data } = useQuery(getClientFeatureQuery("documentScanner"), {
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const [toggleFeature] = useMutation(TOGGLE_CLIENT_FEATURE, {
    refetchQueries: [
      {
        query: getClientFeatureQuery("documentScanner"),
        variables: { id: clientId },
      },
    ],
  });

  const documentScannerEnabled =
    data?.client?.preferences?.documentScanner?.enabled ?? false;

  const onToggleDocumentScanner = useCallback(
    async (enabled: boolean) => {
      try {
        await toggleFeature({
          variables: {
            input: {
              id: clientId,
              feature: "documentScanner",
              enabled,
            },
          },
        });
        toast.success<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}`, {
            feature: "Document Scanner",
          }),
        );
      } catch (e) {
        toast.error<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}Error`, {
            feature: "Document Scanner",
          }),
        );
      }
    },
    [clientId, toggleFeature, t],
  );

  if (!documentScannerEnabled) {
    return (
      <div
        className="tab-pane fade show active"
        id="documentScanner"
        role="tabpanel"
      >
        {isAdmin ? (
          <ToggleFeatureButton
            featureName="Document Scanner"
            onClick={() => onToggleDocumentScanner(true)}
          />
        ) : (
          <div className="text-muted ml-3">
            <h5 className="mb-3">{t("common:premiumFeature.title")}</h5>
            <p>{t("common:premiumFeature.text")}</p>
            <p>{t("common:premiumFeature.contact")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-pane fade show active" id="teams" role="tabpanel">
      <p className="ml-3">{t("common:premiumFeature.documentScannerText")}</p>
      {isAdmin && (
        <div className="mt-3 ml-3">
          <ToggleFeatureButton
            featureName="Document Scanner"
            disable
            onClick={() => onToggleDocumentScanner(false)}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(DocumentScannerFeature);
